// src/components/UserDetailsCard.jsx
import {
  CaretDownOutlined,
  CaretUpOutlined,
  CrownOutlined,
  FileExcelOutlined,
  IdcardOutlined,
  PlusOutlined,
  UserOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { Avatar, Button, Divider, Modal, Select, Tag, Typography } from "antd";
import { saveAs } from "file-saver";
import moment from "moment";
import React, { useState } from "react";
import * as XLSX from "xlsx";
import {
  KYC_STATUS_MAP,
  USER_STATUS_MAP,
  USER_TYPE_MAP,
} from "../../Custom/Custom";
import { generateTransactionExcelReport } from "../../api/TranscationApi";
import { getUsersById } from "../../api/UserAPI";
import { activePrimary, createWallet } from "../../api/WalletApi";
import "./UserDetailsCard.css";

const { Title, Text } = Typography;

export default function UserDetailsCard({ user }) {
  const [showAllWallets, setShowAllWallets] = useState(false);

  const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
  const [newWalletType, setNewWalletType] = useState(null);
  const [mergedUser, setMergedUser] = useState({ ...user });

  mergedUser.kyc = { ...user?.kyc };

  // Ensure wallets is always an array
  const wallets = Array.isArray(mergedUser.wallets) ? mergedUser.wallets : [];

  // Separate primary & others
  const primaryWallet = wallets.find((w) => w.primaryWallet) || wallets[0];
  const otherWallets = wallets.filter((w) => w.id !== primaryWallet.id);

  // Mapped display values
  const userTypeLabel = USER_TYPE_MAP[mergedUser.userType] || "Unknown";
  const userStatus =
    USER_STATUS_MAP[mergedUser.userStatus] || USER_STATUS_MAP[0];

  const kycCode = mergedUser.kyc.status ?? 0;
  const kycStatusData = KYC_STATUS_MAP[kycCode] || KYC_STATUS_MAP[0];

  const downloadReport = () => {
    // Example transaction data
    const transactions = [
      {
        date: "2025-08-14",
        description: "Coffee Purchase",
        amount: -150,
        status: "Completed",
        image: "☕",
      },
      {
        date: "2025-08-13",
        description: "Salary Credit",
        amount: 50000,
        status: "Completed",
        image: "💼",
      },
      {
        date: "2025-08-12",
        description: "Electricity Bill",
        amount: -1200,
        status: "Pending",
        image: "💡",
      },
    ];

    // Convert data to sheet
    const worksheet = XLSX.utils.json_to_sheet(transactions);

    // Create workbook and append sheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");

    // Write the file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    // Save the file
    const data = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(data, "transaction_report.xlsx");
  };

  const downloadReportExcel = () => {
    let obj = {
      userId: "",
    };
    generateTransactionExcelReport(obj).then((response) => {
      if (response.data.size === 0) {
      } else {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Transaction_Report${moment().format(
          "YYYY_MM_DD_HH_mm_sss"
        )}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      }
    });
  };

  const downloadTransactions = () => {
    let obj = { userId: mergedUser.id };

    generateTransactionExcelReport(obj)
      .then((res) => {
        const disposition = res.headers["content-disposition"];
        let fileName = `Transaction_Report${moment().format(
          "YYYY_MM_DD_HH_mm_sss"
        )}.pdf`;
        if (disposition && disposition.indexOf("filename=") !== -1) {
          fileName = disposition.split("filename=")[1].replace(/['"]/g, "");
        }

        const blob = new Blob([res.data], {
          type: res.headers["content-type"],
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        console.error("Download failed:", err);
      });
  };

  const renderWallet = (wallet) => (
    <div
      key={wallet.id}
      className="udc-wallet-main"
      style={{
        border: wallet.primaryWallet
          ? "2px solid #16a34a"
          : "1px solid #d9d9d9",
        borderRadius: 12,
        padding: "16px",
        background: wallet.primaryWallet ? "#f0fdf4" : "#fff",
        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
      }}
    >
      <div className="udc-balance">
        <div
          className="udc-balance-label"
          style={{ fontWeight: 600, marginBottom: 6 }}
        >
          {wallet.primaryWallet ? (
            <>
              Primary Wallet
              <br />
              {wallet.walletName}
            </>
          ) : (
            wallet.walletName || "WALLET"
          )}
        </div>
        <div
          className="udc-balance-amount"
          style={{ fontSize: 18, fontWeight: 700 }}
        >
          ₹ {Number(wallet.totalBalance).toFixed(2)}
        </div>
      </div>

      <div style={{ marginTop: "10px" }}>
        <button
          onClick={() => downloadTransactions(wallet.id)}
          style={{
            background: "#16a34a",
            color: "white",
            padding: "6px 12px",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            width: "100%",
          }}
        >
          <FileExcelOutlined /> Transaction Report
        </button>
      </div>
    </div>
  );

  const openWalletModal = () => setIsWalletModalOpen(true);

  const closeWalletModal = () => {
    setNewWalletType(null);
    setIsWalletModalOpen(false);
  };

  const handleCreateWallet = () => {
    if (!newWalletType) {
      alert("Please select a wallet type");
      return;
    }

    let obj = {
      userId: user.id,
      walletName: newWalletType,
    };

    createWallet(obj).then((response) => {
      closeWalletModal();
      if (response.data.status === 1) {
      }
    });
  };

  const activeWalletPrimary = (id) => {
    let obj = {
      id: id,
    };
    activePrimary(obj).then((response) => {
      if (response.status === 1) {
        getUsersById({ id: user.id }).then((response) => {
          if (response.data.status === 1) {
            setMergedUser(response.data.data.userDetails);
          }
        });
      }
    });
  };

  return (
    <>
      <Modal
        title="Create New Wallet"
        open={isWalletModalOpen}
        onCancel={closeWalletModal}
        footer={null}
        centered
      >
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "block", marginBottom: 8 }}>
            Select Wallet Type
          </label>
          <Select
            placeholder="Choose wallet type"
            style={{ width: "100%" }}
            value={newWalletType}
            onChange={(val) => setNewWalletType(val)}
            options={[
              { label: "Saving", value: "SAVING" },
              { label: "Travel", value: "TRAVEL" },
              { label: "Business", value: "BUSINESS" },
            ]}
          />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <Button onClick={closeWalletModal}>Close</Button>
          <Button type="primary" onClick={handleCreateWallet}>
            Create
          </Button>
        </div>
      </Modal>
      <div className="udc-card">
        {/* Profile */}
        <div className="udc-section udc-profile">
          <div className="udc-avatar-wrap">
            <Avatar
              size={72}
              icon={<UserOutlined />}
              src={mergedUser.avatarUrl || null}
            />
          </div>
          <div className="udc-profile-info">
            <Title level={4} className="udc-name">
              {mergedUser.name}
            </Title>
            <Text type="secondary" className="udc-email">
              {mergedUser.email}
            </Text>
            <div className="udc-phone">{mergedUser.mobileNo}</div>

            {/* User Type & Status */}
            <div style={{ marginTop: "8px" }}>
              <Tag color="blue">
                <CrownOutlined /> {userTypeLabel}
              </Tag>
              <Tag color={userStatus.color}>{userStatus.label}</Tag>
            </div>
          </div>
        </div>

        <Divider />

        {/* Wallet */}

        <div style={{ marginBottom: "16px", textAlign: "right" }}>
          <div
            className="d-flex justify-content-end gap-1"
            style={{
              overflowX: "auto", // 👈 enable horizontal scroll
              whiteSpace: "nowrap", // 👈 prevent wrapping
            }}
          >
            {otherWallets.length > 0 && (
              <div
                style={{ cursor: "pointer", color: "#2563eb", fontWeight: 500 }}
                onClick={() => setShowAllWallets(!showAllWallets)}
              >
                {showAllWallets ? (
                  <Button
                    type="primary"
                    icon={<CaretUpOutlined />}
                    style={{ borderRadius: 6 }}
                  >
                    Hide Other Wallets
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    icon={<CaretDownOutlined />}
                    style={{ borderRadius: 6 }}
                  >
                    Show All Wallets
                  </Button>
                )}
              </div>
            )}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openWalletModal}
              style={{ borderRadius: 6 }}
            >
              Create Wallet
            </Button>
          </div>
        </div>

        <div
          className="udc-section udc-wallet"
          style={{
            display: "flex",
            flexDirection: "column", // 👈 stack vertically
            gap: "16px",
          }}
        >
          <div
            className="udc-section-header"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <WalletOutlined />{" "}
              <span className="udc-section-title">Wallets</span>
            </div>
          </div>

          {/* Always show primary wallet */}
          <div>{renderWallet(primaryWallet)}</div>

          {/* Expand other wallets only when clicked */}
          {showAllWallets && otherWallets.length > 0 && (
            <div
              className="udc-other-wallets"
              style={{
                display: "flex",
                flexDirection: "column", // 👈 stack vertically
                gap: "16px",
                padding: "8px 0",
              }}
            >
              {otherWallets.map((wallet) => (
                <div
                  key={wallet.id}
                  style={{
                    width: "100%", // full width, matches primary
                  }}
                  onClick={() => activeWalletPrimary(wallet.id)}
                >
                  {renderWallet(wallet)}
                </div>
              ))}
            </div>
          )}
        </div>

        <Divider />

        {/* KYC */}
        <div className="udc-section udc-kyc">
          <div className="udc-section-header">
            <IdcardOutlined /> <span className="udc-section-title">KYC</span>
          </div>

          <div className="udc-kyc-body">
            <div className="udc-kyc-row">
              <div className="udc-kyc-label">Status</div>
              <Tag className="udc-kyc-tag" color={kycStatusData.color}>
                {kycStatusData.label}
              </Tag>
            </div>

            {/* <div className="udc-kyc-row">
            <div className="udc-kyc-label">ID Type</div>
            <div className="udc-kyc-value">{mergedUser.kyc.documentType}</div>
          </div> */}

            <div className="udc-kyc-row">
              <div className="udc-kyc-label">Submitted Date</div>
              <div className="udc-kyc-value">
                {new Date(mergedUser.kyc.createdDate).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
